import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { HTTPInstance } from '../core';
import { useApiMutation, useApiQuery } from './base';
import {
  MigrationRecord,
  CreateMigrationRecordPayload,
  UpdateMigrationRecordPayload,
} from '../types/migration';

export const useCreateMigrationRecord = () => {
  return useApiMutation<MigrationRecord, CreateMigrationRecordPayload>(
    '/migration-records',
    'POST',
  );
};

export const useGetMigrationRecord = (recordId: string) => {
  return useQuery<MigrationRecord | null, AxiosError>({
    queryKey: ['migrationRecords', recordId],
    enabled: !!recordId,
    queryFn: async () => {
      try {
        const response = await HTTPInstance.get<MigrationRecord>(
          `/migration-records/${recordId}`,
        );
        return response.data ?? null;
      } catch (error) {
        const status = (error as AxiosError)?.response?.status;
        if (status === 404) {
          return null;
        }
        throw error;
      }
    },
  });
};

export const useGetMigrationRecordsByTreeId = (treeId: string) => {
  return useApiQuery<MigrationRecord[]>(
    ['migrationRecords', treeId],
    `/migration-records/${treeId}/tree`,
    {
      queryKey: ['migrationRecords', treeId],
      enabled: !!treeId,
    },
  );
};

export const useGetMigrationRecordByUser = (userId: string) => {
  return useApiQuery<MigrationRecord[]>(
    ['migrationRecords', userId],
    `/migration-records/${userId}/user`,
    {
      queryKey: ['migrationRecords', userId],
      enabled: !!userId,
    },
  );
};

export const useUpdateMigrationRecord = (recordId: string) => {
  return useApiMutation<MigrationRecord, UpdateMigrationRecordPayload>(
    `/migration-records/${recordId}/update`,
    'PUT',
  );
};

export const useDeleteMigrationRecord = (recordId: string) => {
  return useApiMutation<void, void>(`/migration-records/${recordId}`, 'DELETE');
};
